import { useIntl } from 'react-intl';

import { OverlayTooltip } from '../overlay-tooltip/OverlayTooltip.js';
import { useThresholdBarTooltip } from './hooks/useThresholdBarTooltip.js';
import { CircleMarker } from './markers/CircleMarker.js';
import { MARKER_OPACITY, MARKER_SHADOW_OPACITY } from './markers/constants.js';
import { PillMarker } from './markers/PillMarker.js';
import { wrapper } from './styles.sty.js';
import type { ThresholdBarProps } from './types.js';
import { THRESHOLD_BAR_THICKNESS } from './types/constants.js';
import { getThresholdIntValue } from './utils/get-threshold-int-value.js';
import { resolveInfinitePixel } from './utils/resolve-infinite-pixel.js';
import { sortMarkers } from './utils/sort-circle-markers.js';

type LayoutType =
  | 'vertical-flip'
  | 'vertical-no-flip'
  | 'horizontal-flip'
  | 'horizontal-no-flip';

export const ThresholdBarTemplate = (props: ThresholdBarProps) => {
  const {
    borderArea,
    length,
    markers,
    layout = 'vertical',
    isFlipped = false,
    onHover,
  } = props;

  const sortedMarkers = markers.toSorted(sortMarkers);
  const intl = useIntl();

  const {
    showTooltip,
    tooltipPosition,
    tooltipItems,
    handleMouseMove,
    handleMouseLeave,
  } = useThresholdBarTooltip(sortedMarkers, onHover);

  const flipType = isFlipped ? 'flip' : 'no-flip';
  const layoutType: LayoutType = `${layout}-${flipType}`;

  return (
    <div
      className={wrapper({
        borderArea,
        layoutType,
      })}
      style={{ height: length }}
    >
      <svg width={THRESHOLD_BAR_THICKNESS} height={length}>
        {sortedMarkers.map((marker, index) => {
          const { y0, y1, color, isShadow = false } = marker;
          const isMarkerARange = y0 !== y1;
          const resolvedY0 = resolveInfinitePixel(y0, length);
          const resolvedY1 = resolveInfinitePixel(y1, length);

          if (isMarkerARange) {
            return [
              <PillMarker
                x={4}
                y0={resolvedY0}
                y1={resolvedY1}
                color={color}
                opacity={isShadow ? MARKER_SHADOW_OPACITY : MARKER_OPACITY}
                key={`threshold-bar-pill-marker-${marker.id}`}
                onMouseMove={(event) => handleMouseMove(event, index)}
                onMouseLeave={handleMouseLeave}
              />,
            ];
          } else {
            return [
              <CircleMarker
                x={8}
                y={resolvedY0}
                color={color}
                key={`threshold-bar-circle-marker-${marker.id}`}
                onMouseMove={(event) => handleMouseMove(event, index)}
                onMouseLeave={handleMouseLeave}
              />,
            ];
          }
        })}
      </svg>
      {showTooltip && tooltipPosition && (
        <OverlayTooltip
          isInteractive={true}
          absoluteX={tooltipPosition.x}
          absoluteY={tooltipPosition.y}
        >
          {tooltipItems.map(({ id, name, color, values }) => {
            return (
              <OverlayTooltip.SeriesItem
                key={id}
                color={color.Default}
                name={name}
                value={`${getThresholdIntValue(intl, values)}`}
              />
            );
          })}
        </OverlayTooltip>
      )}
    </div>
  );
};

ThresholdBarTemplate['displayName'] = 'ThresholdBarTemplate';
